import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, User, Shield, Ban, Eye, DollarSign, TrendingUp, AlertTriangle, CheckCircle, XCircle, Calendar, Globe, Smartphone, Mail, Clock, Activity, ChevronDown, RefreshCw, ChevronLeft, ChevronRight, Phone, ExternalLink } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { MdJoinLeft, MdTimelapse } from 'react-icons/md';

const AlertModal = ({ show, title, onConfirm, onCancel, children }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-w-sm w-full">
        <h3 className="text-lg font-bold mb-3 text-gray-200">{title}</h3>
        <div className="mb-4 text-gray-300 text-sm">
          {children}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

function PlutoUser({ second_page = 12345 }) {
  const [userData, setUserData] = useState({});
  const [fingerprints, setUserFingerPrints] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [offersMade, setOffersMade] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [category, setCategory] = useState(0);
  const [rank, setRank] = useState(0);
  const [banRate, setBanRate] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [banReason, setBanReason] = useState('Fraudulent Behaviour');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [alert, setAlert] = useState({ show: false, user_id: -1, offer_id: -1, name: '' });
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAdvancedData, setShowAdvancedData] = useState(false);
  
  // Pagination states for advanced data
  const [fingerprintPage, setFingerprintPage] = useState(1);
  const [ipPage, setIpPage] = useState(1);
  const [advancedDataPerPage] = useState(5);
  const PaginationButton = ({ page, active, onClick, disabled, children }) => (
    <button
      onClick={() => onClick(page)}
      disabled={disabled}
      className={`px-1 py-1 text-sm font-medium rounded-md ${
        active
          ? 'bg-blue-600 text-white'
          : disabled
          ? 'bg-gray-900 text-gray-200 cursor-not-allowed'
          : 'bg-gray-900 text-gray-300 hover:bg-gray-800 '
      }`}
    >
      {children || page}
    </button>
  );
  const getLevelColor = (level) => {
    if (level >= 8) return 'bg-purple-500';
    if (level >= 5) return 'bg-blue-500';
    if (level >= 3) return 'bg-green-500';
    return 'bg-gray-500';
  };

  const numberWithCommas = (x) => {
    return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || '0';
  };

  const loadCategoryData = useCallback((cat, page = 1) => {
    setCategoryLoading(true);
    setOffersMade([]);
    
    socket.emit('getUserAdminData', {
      user: second_page,
      page: page,
      cat: cat
    });

    const handleCategoryResponse = (data) => {
      setCategoryLoading(false);
      setOffersMade(data.data);
      setTotalPages(Math.ceil(parseInt(data.length) / 5));
      setCurrentPage(page);
      socket.off('response_get_admin_user_data', handleCategoryResponse);
    };

    socket.on('response_get_admin_user_data', handleCategoryResponse);
  }, [second_page]);

  useEffect(() => {
    setLoading(true);
    
    socket.on('response_get_admin_user_fingerprints', (data) => {
      setUserFingerPrints(data.fingerprints);
    });

    socket.on('response_get_admin_user', (data) => {
      setUserData(data);
      setUserProfile(data.user);
      setLoading(false);
    });

    socket.on('response_get_admin_user_data', (data) => {
      setBanRate(data.banRate);
      setRank(data.rank);
    });

    socket.emit('getUserAdmin', { user_id: second_page });
    loadCategoryData(0, 1);

    return () => {
      socket.off('response_get_admin_user_fingerprints');
      socket.off('response_get_admin_user');
      socket.off('response_get_admin_user_data');
    };
  }, [second_page, loadCategoryData]);

  const handleCategoryChange = useCallback((newCategory) => {
    if (newCategory !== category) {
      setCategory(newCategory);
      setSearchTerm('');
      setStatusFilter('all');
      setCurrentPage(1);
      loadCategoryData(newCategory, 1);
    }
  }, [category, loadCategoryData]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      loadCategoryData(category, newPage);
    }
  }, [category, currentPage, totalPages, loadCategoryData]);

  // Pagination calculations for advanced data
  const paginatedFingerprints = useMemo(() => {
    const startIndex = (fingerprintPage - 1) * advancedDataPerPage;
    return fingerprints.slice(startIndex, startIndex + advancedDataPerPage);
  }, [fingerprints, fingerprintPage, advancedDataPerPage]);

  const paginatedIps = useMemo(() => {
    const startIndex = (ipPage - 1) * advancedDataPerPage;
    return userProfile.ipUsedData?.slice(startIndex, startIndex + advancedDataPerPage) || [];
  }, [userProfile.ipUsedData, ipPage, advancedDataPerPage]);

  const fingerprintTotalPages = Math.ceil(fingerprints.length / advancedDataPerPage);
  const ipTotalPages = Math.ceil((userProfile.ipUsedData?.length || 0) / advancedDataPerPage);

  const banReasons = [
    'Fraudulent Behaviour', 'Temp Email Address', 'Multiple Accounts (Same Username or Similar Email)',
    'Fraudulent Offers', 'Fraudulent Surveys', 'High Fraud Rate', 'High Chargeback Rate',
    'Multiple Accounts', 'VPN/Proxy Usage', 'Speedy Offers',
    'Country Phone Number Different Than Country Offers Completed',
    'Country Account Location Different Than Country Offers Completed',
    'Too many countries used for offers completion'
  ];

  const handleReleaseOffer = (userId, offerId, offerName) => {
    setAlert({
      show: true,
      user_id: userId,
      offer_id: offerId,
      name: offerName,
      title: 'Release Offer'
    });
  };

  const confirmReleaseOffer = () => {
    socket.emit('adminAction', {
      action: 'releaseOfferAdmin',
      user_id: alert.user_id,
      offer_id: alert.offer_id,
    });
    setAlert({ show: false, user_id: -1, offer_id: -1, name: '', title: '' });
    loadCategoryData(category, currentPage);
  };

  const handleAction = (action, additionalData = {}) => {
    socket.emit('adminAction', {
      action,
      user: userProfile.id,
      ...additionalData
    });
  };

  const renderStatusCell = (offer) => {
    if (category === 2) {
      if (offer.wall_name === "Direct Wallet") {
        return offer.paid === "No" ? 'None' : (
          <a 
            href={`https://blockchair.com/${offer.name.toLowerCase().split(" ").join("-")}/transaction/${offer.status}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 text-xs"
          >
            TX <ExternalLink className="h-3 w-3 inline" />
          </a>
        );
      }
      return offer.status ? offer.status.substr(0, 4) + "..." : '';
    } else {
      if (offer.status === "Pending") {
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="text-xs">Pending</span>
              <button
                onClick={() => handleReleaseOffer(userProfile.id, offer.id, offer.name)}
                className="text-blue-400 hover:text-blue-300 text-xs underline"
              >
                Release
              </button>
            </div>
            <span className="text-gray-500 text-xs">
              {offer.release_time?.split(' ').join('_')}
            </span>
          </div>
        );
      }
      return offer.status;
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', alert = false }) => (
    <div className={`bg-gray-800 rounded-lg p-2 border ${alert ? 'border-red-500/50 bg-red-900/20' : 'border-gray-700'}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-gray-400 text-xs font-medium">{title}</p>
          <p className={`font-bold text-sm ${alert ? 'text-red-400' : `text-${color}-400`}`}>
            {value}
          </p>
        </div>
        <Icon className={`${alert ? 'text-red-400' : `text-${color}-400`} h-4 w-4 flex-shrink-0`} />
      </div>
    </div>
  );

  const PaginationControls = ({ currentPage, totalPages, onPageChange, disabled = false }) => {
    let pages = []
     for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    return (
    
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-400">Page {currentPage} of {totalPages}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || disabled}
          className="p-1 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-3 w-3" />
        </button>
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || disabled}
          className="p-1 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
  const renderPagination = () => {
    const pages = [];
    
    // Always show first page
    if (totalPages > 1) pages.push(1);
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    
    // Always show last page
    if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);

    return (
      <div className="flex items-center justify-between px-6 py-3  ">
        <div className="flex items-center text-sm text-gray-300">
          <span className="text-gray-400">Page {currentPage} of {totalPages}</span>
        </div>
        <div className="flex items-center space-x-1">
          <PaginationButton
            page={currentPage - 1}
            onClick={handlePageChange}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </PaginationButton>
          
          {pages.map((page, index) => (
            <React.Fragment key={page}>
              {index > 0 && pages[index - 1] !== page - 1 && (
                <span className="px-2 text-gray-500">...</span>
              )}
              <PaginationButton
                page={page}
                active={page === currentPage}
                onClick={handlePageChange}
              />
            </React.Fragment>
          ))}
          
          <PaginationButton
            page={currentPage + 1}
            onClick={handlePageChange}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </PaginationButton>
        </div>
      </div>
    );
  };


  const categories = [
    { id: 0, name: 'Offers', icon: '📋' },
    { id: 1, name: 'Earnings', icon: '💰' },
    { id: 2, name: 'Withdrawals', icon: '💳' },
    { id: 3, name: 'Chargebacks', icon: '⚠️' }
  ];

  const filteredOffers = useMemo(() => {
    return offersMade.filter(offer => {
      const matchesSearch = offer.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      const matchesStatus = statusFilter === 'all' || offer.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [offersMade, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <div className="flex items-center space-x-3">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-400" />
          <span className="text-sm">Loading user data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <AlertModal
        show={alert.show}
        title={alert.title}
        onConfirm={confirmReleaseOffer}
        onCancel={() => setAlert({ show: false, user_id: -1, offer_id: -1, name: '', title: '' })}
      >
        <div className="space-y-2">
          <p><strong>Offer:</strong> {alert.name}</p>
          <p><strong>Offer ID:</strong> {alert.offer_id}</p>
          <p><strong>User ID:</strong> {alert.user_id}</p>
        </div>
      </AlertModal>

      {/* Compact Mobile Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="p-3">
          <div className="flex items-center gap-3">
            <img
              src={userProfile.profilePic || '/api/placeholder/40/40'}
              alt="Profile"
              className="w-10 h-10 rounded-full border-2 border-gray-600"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-lg font-bold truncate">{userProfile.username}</h1>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLevelColor(userProfile.level)} text-white`}>
                  L{userProfile.level}
                </span>
                {userProfile.banned && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white">
                    Banned
                  </span>
                )}
              </div>
              
              {/* Compact User Info */}
              <div className="text-xs text-gray-400 space-y-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-32">{userProfile.email}</span>
                    {userProfile.emailVerified && <CheckCircle className="h-3 w-3 text-green-400" />}
                  </div>
                  <div className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    <span>{userProfile.country}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    <span>{userProfile.phone_verify}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <MdJoinLeft className="h-3 w-3" />
                  <span>Joined {userProfile.joinDate}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MdTimelapse className="h-3 w-3" />
                  <span>Online {userProfile.lastOnline} ago</span>
                </div>
              </div>
                </div>
                
                {/* <button
                  onClick={() => setShowUserDetails(!showUserDetails)}
                  className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                >
                  <span>More details</span>
                  <ChevronDown className={`h-3 w-3 transition-transform ${showUserDetails ? 'rotate-180' : ''}`} />
                </button> */}
                 {/* Admin Actions */}
              {rank >= 8 && (
                <div className="space-y-2 pt-2">
                  {/* Ban/Unban Section */}
                  <div className="flex ">
                    {!userProfile.banned && (
                      <select
                        value={banReason}
                        onChange={(e) => setBanReason(e.target.value)}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs"
                      >
                        {banReasons.slice(0, 6).map(reason => (
                          <option key={reason} value={reason}>
                            {(reason+'').substring(0, 30)}...
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={() => handleAction('ban_user', { reason: banReason })}
                      className={`rounded text-xs transition-colors ${
                        userProfile.banned 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {!userProfile.banned ? 'Ban' : 'Unban'}
                    </button>
                  </div>
                  </div>
              )}
              </div>
            </div>
          </div>

          {/* Expandable User Details */}
          {showUserDetails && (
            <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
            

              {/* Admin Actions */}
              {rank >= 8 && (
                <div className="space-y-2 pt-2">
                  {/* Ban/Unban Section */}
                 

                  {/* Additional Admin Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Email Verification */}
                    <button
                      onClick={() => handleAction(userProfile.emailVerified ? 'unverify_email' : 'verify_email')}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        userProfile.emailVerified
                          ? 'bg-orange-600 hover:bg-orange-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {userProfile.emailVerified ? 'Unverify Email' : 'Verify Email'}
                    </button>

                    {/* Phone Verification */}
                    <button
                      onClick={() => handleAction(userProfile.phone_verify === 'Yes' ? 'unverify_phone' : 'verify_phone')}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        userProfile.phone_verify === 'Yes'
                          ? 'bg-orange-600 hover:bg-orange-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {userProfile.phone_verify === 'Yes' ? 'Unverify Phone' : 'Verify Phone'}
                    </button>

                    {/* Reset Password */}
                    <button
                      onClick={() => handleAction('reset_password')}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                    >
                      Reset Password
                    </button>

                    {/* Clear Balance */}
                    <button
                      onClick={() => handleAction('clear_balance')}
                      className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs transition-colors"
                    >
                      Clear Balance
                    </button>

                    {/* Lock/Unlock Account */}
                    <button
                      onClick={() => handleAction(userProfile.locked ? 'unlock_account' : 'lock_account')}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        userProfile.locked
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      }`}
                    >
                      {userProfile.locked ? 'Unlock' : 'Lock'} Account
                    </button>

                    {/* Delete Account */}
                    <button
                      onClick={() => handleAction('delete_account')}
                      className="px-2 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-xs transition-colors"
                    >
                      Delete Account
                    </button>
                  </div>

                  {/* Additional Tools */}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => handleAction('reset_level')}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors"
                    >
                      Reset Level
                    </button>
                    <button
                      onClick={() => handleAction('clear_referrals')}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors"
                    >
                      Clear Refs
                    </button>
                    <button
                      onClick={() => handleAction('force_logout')}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors"
                    >
                      Force Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Compact Stats Grid */}
        <div className="grid grid-cols-3 gap-2">
          <StatCard
            title="Balance"
            value={`$${numberWithCommas(userProfile.balance)}`}
            icon={DollarSign}
            color="green"
          />
          <StatCard
            title="Earned"
            value={`$${numberWithCommas(userProfile.totalEarned)}`}
            icon={TrendingUp}
            color="blue"
          />
          <StatCard
            title="Withdrawn"
            value={`$${numberWithCommas(userProfile.withdrawAmount)}`}
            icon={DollarSign}
            color="purple"
          />
          <StatCard
            title="Ban Rate"
            value={`${banRate}%`}
            icon={AlertTriangle}
            color={banRate > 10 ? "red" : "yellow"}
            alert={banRate > 10}
          />
          <StatCard
            title="CB Rate"
            value={`${userData.cbRate}%`}
            icon={AlertTriangle}
            color="red"
            alert={userData.cbRate > 20}
          />
          <StatCard
            title="VPN"
            value={userProfile.usedVPNForOffers === 'Yes' ? 'Yes' : 'No'}
            icon={Shield}
            color={userProfile.usedVPNForOffers === 'Yes' ? 'red' : 'green'}
            alert={userProfile.usedVPNForOffers === 'Yes'}
          />
        </div>

        {/* Ban Alert */}
        {userProfile.banned && (
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm">
              <Ban className="h-4 w-4 text-red-400 flex-shrink-0" />
              <span className="font-medium text-red-400">Banned:</span>
              <span className="text-gray-300 text-xs">{userProfile.banReason}</span>
            </div>
          </div>
        )}

        {/* Data Section */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          {/* Category Tabs - Mobile Friendly */}
          <div className="border-b border-gray-700 p-3">
            <div className="grid grid-cols-4 gap-1">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  disabled={categoryLoading}
                  className={`flex flex-col items-center gap-1 px-2 py-2 rounded text-xs transition-all ${
                    category === cat.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                  } ${categoryLoading ? 'opacity-50' : ''}`}
                >
                  <span className="text-sm">{cat.icon}</span>
                  <span>{cat.name}</span>
                  {categoryLoading && category === cat.id && (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Search and Filter - Compact */}
          <div className="p-3 border-b border-gray-700">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded px-2 py-2 text-sm"
              >
                <option value="all">All</option>
                <option value="Pending">Pending</option>
                <option value="Credited">Credited</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Mobile Cards / Desktop Table */}
          <div className="overflow-hidden">
            {categoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-blue-400" />
                <span className="ml-2 text-gray-400 text-sm">Loading...</span>
              </div>
            ) : (
              <>
                {/* Mobile Cards */}
                <div className="md:hidden p-3 space-y-3">
                  {filteredOffers.map((offer, i) => (
                    <div key={offer.id || i} className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                      {/* Header with Name and Amount */}
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-sm text-gray-200 truncate flex-1 mr-2" title={offer.name}>
                          {offer.name}
                        </h4>
                        <div className="text-right">
                          <div className="text-green-400 font-semibold text-sm">
                            {offer.amount.split('(')[0]}
                          </div>
                          {offer.amount.includes('(') && (
                            <div className="text-gray-500 text-xs">
                              {offer.amount.split('(')[1].split(')')[0]}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Basic Info Grid */}
                      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <span className="text-gray-300 ml-1">{offer.date}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Wall:</span>
                          <span className="text-gray-300 ml-1 truncate" title={offer.wall_name}>
                            {offer.wall_name}
                          </span>
                        </div>
                      </div>

                      {/* Category-specific data */}
                   {(category === 0 || category === 3) && (
  <div className="grid grid-cols-2 gap-2 text-xs mb-2">
    <div>
      <span className="text-gray-500">CB Rate:</span>
      <span className={`ml-1 ${
        parseFloat(offer.cb) > 20 ? 'text-red-400' : 
        parseFloat(offer.cb) > 10 ? 'text-yellow-400' : 'text-green-400'
      }`}>
        {offer.cbRate}
      </span>
    </div>
    <div>
      <span className="text-gray-500">Ban Rate:</span>
      <span className="text-gray-300 ml-1">{offer.banRate}</span>
    </div>
    <div>
      <span className="text-gray-500">Flag:</span>
      <span className="ml-1">
        <span className={`w-2 h-2 rounded-full inline-block ${
          offer.shouldBanX ? 'bg-red-500' : 'bg-green-500'
        }`}></span>
        <span className="ml-1 text-gray-300">
          {offer.shouldBanX ? 'Flagged' : 'Clean'}
        </span>
      </span>
    </div>
    <div>
      <span className="text-gray-500">Country:</span>
      <span className="text-gray-300 ml-1">{offer.ip}</span>
    </div>
  </div>
)}


                      {/* Status and Verification Row */}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-600">
                        <div className="text-xs">
                          <div className="text-gray-500 mb-1">Status:</div>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            offer.status === 'Credited' ? 'bg-green-900/50 text-green-300' :
                            offer.status === 'Pending' ? 'bg-yellow-900/50 text-yellow-300' :
                            offer.status === 'Completed' ? 'bg-blue-900/50 text-blue-300' :
                            'bg-red-900/50 text-red-300'
                          }`}>
                            {renderStatusCell(offer)}
                          </div>
                        </div>
                        
                        {/* Verification section for offers/chargebacks */}
                        {(category === 0 || category === 3) && (
                          <div className="text-xs text-right">
                            <div className="text-gray-500 mb-1">Verified:</div>
                            <div className={offer.verified ? 'text-green-400' : 'text-yellow-400'}>
                              <span className="block mb-1">
                                {offer.verified ? '✓ Yes' : '✗ No'}
                              </span>
                              <div
                                onClick={() => {
                                  const action = offer.verified ? 'remove_trustedoffer' : 'add_trustedoffer';
                                  socket.emit('adminAction', {
                                    action,
                                    user: userProfile.id,
                                    name: offer.name,
                                    wall: offer.wall_name,
                                  });
                                  loadCategoryData(category, currentPage);
                                }}
                                className="text-blue-400 hover:text-blue-300 text-xs underline"
                              >
                                {offer.verified ? 'Remove' : 'Add'}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* For withdrawals - show transaction link if available */}
                        {category === 2 && offer.wall_name === "Direct Wallet" && offer.paid !== "No" && (
                          <div className="text-xs">
                            <a 
                              href={`https://blockchair.com/${offer.name.toLowerCase().split(" ").join("-")}/transaction/${offer.status}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              View TX <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Additional details for specific categories */}
                      {category === 2 && offer.status && offer.wall_name !== "Direct Wallet" && (
                        <div className="pt-2 border-t border-gray-600 mt-2">
                          <div className="text-xs">
                            <span className="text-gray-500">Transaction:</span>
                            <span className="text-gray-300 ml-1 font-mono text-xs break-all">
                              {offer.status.length > 20 ? `${offer.status.substr(0, 20)}...` : offer.status}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Release time for pending offers */}
                      {offer.status === "Pending" && offer.release_time && (
                        <div className="pt-2 border-t border-gray-600 mt-2">
                          <div className="text-xs">
                            <span className="text-gray-500">Release Time:</span>
                            <span className="text-gray-400 ml-1">
                              {offer.release_time.split(' ').join('_')}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-800/30">
                        <th className="text-left py-2 px-3 font-medium">Name</th>
                        {(category === 0 || category === 3) && (
                          <>
                            <th className="text-left py-2 px-3 font-medium">CB%</th>
                            <th className="text-left py-2 px-3 font-medium">Ban%</th>
                            <th className="text-left py-2 px-3 font-medium">Flag</th>
                          </>
                        )}
                        <th className="text-left py-2 px-3 font-medium">Amount</th>
                        <th className="text-left py-2 px-3 font-medium">Date</th>
                        <th className="text-left py-2 px-3 font-medium">Wall</th>
                        <th className="text-left py-2 px-3 font-medium">Status</th>
                        {(category === 0 || category === 3) && (
                          <>
                            <th className="text-left py-2 px-3 font-medium">Country</th>
                            <th className="text-left py-2 px-3 font-medium">Verified</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOffers.map((offer, i) => (
                        <tr key={offer.id || i} className="border-b border-gray-700/30 hover:bg-gray-700/20">
                          <td className="py-2 px-3 font-medium max-w-[200px] truncate" title={offer.name}>
                            {offer.name}
                          </td>
                          {(category === 0 || category === 3) && (
                            <>
                              <td className={`py-2 px-3 ${
                                parseFloat(offer.cb) > 20 ? 'text-red-400' : 
                                parseFloat(offer.cb) > 10 ? 'text-yellow-400' : 'text-green-400'
                              }`}>
                                {offer.cbRate}
                              </td>
                              <td className="py-2 px-3">{offer.banRate}</td>
                              <td className="py-2 px-3">
                                <span className={`w-2 h-2 rounded-full inline-block ${
                                  offer.shouldBanX ? 'bg-red-500' : 'bg-green-500'
                                }`}></span>
                              </td>
                            </>
                          )}
                          <td className="py-2 px-3 font-semibold">
                            <div className="text-green-400">{offer.amount.split('(')[0]}</div>
                            {offer.amount.includes('(') && (
                              <div className="text-gray-500">{offer.amount.split('(')[1].split(')')[0]}</div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-gray-400">{offer.date}</td>
                          <td className="py-2 px-3 max-w-[100px] truncate" title={offer.wall_name}>
                            {offer.wall_name}
                          </td>
                          <td className="py-2 px-3">
                            {renderStatusCell(offer)}
                          </td>
                          {(category === 0 || category === 3) && (
                            <>
                              <td className="py-2 px-3 max-w-[70px] truncate">{offer.ip}</td>
                              <td className="py-2 px-3">
                                <div className={offer.verified ? 'text-green-400' : 'text-yellow-400'}>
                                  {offer.verified ? 'Yes ' : 'No '}
                                  <button
                                    onClick={() => {
                                      const action = offer.verified ? 'remove_trustedoffer' : 'add_trustedoffer';
                                      socket.emit('adminAction', {
                                        action,
                                        user: userProfile.id,
                                        name: offer.name,
                                        wall: offer.wall_name,
                                      });
                                      loadCategoryData(category, currentPage);
                                    }}
                                    className="text-blue-400 hover:text-blue-300 text-xs underline"
                                  >
                                    {offer.verified ? 'Remove' : 'Add'}
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {filteredOffers.length === 0 && !categoryLoading && (
              <div className="py-8 px-3 text-center text-gray-400 text-sm">
                No data found
              </div>
            )}
          </div>

          {/* Compact Pagination */}
          {renderPagination()}
          {/* {totalPages > 1 && (
            <div className="border-t border-gray-700 p-3">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                disabled={categoryLoading}
              />
            </div>
          )} */}
        </div>

        {/* Advanced Data Toggle */}
        {rank >= 8 && (fingerprints.length > 0 || userProfile.ipUsedData?.length > 0) && (
          <button
            onClick={() => setShowAdvancedData(!showAdvancedData)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-left hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Advanced Data</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedData ? 'rotate-180' : ''}`} />
            </div>
          </button>
        )}

        {/* Advanced Data - Collapsible */}
        {true==true && rank >= 8 && (
          <div className="space-y-3">
            {/* Fingerprints */}
            {fingerprints.length > 0 && (
              <div className="bg-gray-800 rounded-lg border border-gray-700">
                <div className="p-3 border-b border-gray-700">
                  <h3 className="text-sm font-bold flex items-center">
                    <Activity className="h-4 w-4 mr-2" />
                    Device Fingerprints ({fingerprints.length})
                  </h3>
                </div>
                
                <div className="p-3 space-y-2">
                  {paginatedFingerprints.map((fp, index) => (
                    <div key={fp.id || index} className="bg-gray-700/30 rounded p-2 text-xs">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-gray-300 truncate mb-1" title={fp.fingerprint}>
                            {(fp.fingerprint+'').substring(0, 40)}...
                          </div>
                          <div className="flex items-center gap-2 text-gray-500">
                            <span>IP: {fp.ip}</span>
                            <span>•</span>
                            <span>Count: {fp.count}</span>
                          </div>
                        </div>
                        <a
                          href={`/pluto/fingerprint/${encodeURIComponent(fp.fingerprint)}`}
                          target="_blank"
                          className="text-blue-400 hover:text-blue-300 flex items-center gap-1 ml-2 flex-shrink-0"
                        >
                          <span>View</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Fingerprints Pagination */}
                {fingerprintTotalPages > 1 && (
                  <div className="border-t border-gray-700 p-3">
                    <PaginationControls
                      currentPage={fingerprintPage}
                      totalPages={fingerprintTotalPages}
                      onPageChange={setFingerprintPage}
                    />
                  </div>
                )}
              </div>
            )}

            {/* IP Data */}
            {userProfile.ipUsedData?.length > 0 && (
              <div className="bg-gray-800 rounded-lg border border-gray-700">
                <div className="p-3 border-b border-gray-700">
                  <h3 className="text-sm font-bold flex items-center">
                    <Globe className="h-4 w-4 mr-2" />
                    IP Usage Data ({userProfile.ipUsedData.length})
                  </h3>
                </div>
                
                <div className="p-3 space-y-2">
                  {paginatedIps.map((ipData, index) => (
                    <div key={ipData.id || index} className="bg-gray-700/30 rounded p-2 text-xs">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-gray-300">{ipData.ip}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ipData.ipData.isVPN ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'
                            }`}>
                              {ipData.ipData.isVPN ? 'VPN' : 'Clean'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-500">
                            <span>Score: {ipData.ipData.score}</span>
                            <span>•</span>
                            <span>Users: {ipData.user.length}</span>
                          </div>
                        </div>
                        <a
                          href={`/pluto/ip/${encodeURIComponent(ipData.ip.split(' (')[0])}`}
                          target="_blank"

                          className="text-blue-400 hover:text-blue-300 flex items-center gap-1 ml-2 flex-shrink-0"
                        >
                          <span>View</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                {/* IP Pagination */}
                {ipTotalPages > 1 && (
                  <div className="border-t border-gray-700 p-3">
                    <PaginationControls
                      currentPage={ipPage}
                      totalPages={ipTotalPages}
                      onPageChange={setIpPage}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlutoUser